/**
 * iDecide - Iframe plugin
 * Adds embeddable iframe overlays that are positioned by an Imgly block.
 */

import CreativeEditorSDK, {type AssetResult} from '@cesdk/cesdk-js';
import type {CreativeEngine} from '@cesdk/cesdk-js';
import {BLOCK_TYPE, IFRAME_META_KEY} from "../constants.ts";
import {isEngineReady} from "../utils/utilities.ts";
import {positionOverlayToBlock, updateOverlayTimelineVisibility} from "../utils/interactionOverlay.ts";
import {findOwningPage} from "../utils/slideUtilities.ts";
import {normalizeResolvedUrl} from "../utils/url.ts";

export type IframeMeta = {
    id: string;
    elementType: string;
    url?: string;
    style?: {
        borderRadiusPx?: number;
        backgroundColor?: string;
        outlineColor?: string;
        outlineSizePx?: number;
    };
};

type RectCornerRadii = {
    tl: number;
    tr: number;
    br: number;
    bl: number;
};

type SupportedIframeShapeType = 'rect' | 'ellipse' | 'polygon' | 'star';

type IframeShapeVisual = {
    type: SupportedIframeShapeType | 'unsupported';
    borderRadiusCss: string;
    clipPathCss: string | null;
};

const IFRAME_SOURCE_ID = 'idecide.iframe.source';
export const IFRAME_ENTRY_ID = 'idecide.iframes';
const INVALID_BLOCK_ID = 4294967295;

const IFRAME_OVERLAYS_BY_ENGINE = new WeakMap<CreativeEngine, Map<number, HTMLDivElement>>();

const getIframeOverlays = (engine: CreativeEngine): Map<number, HTMLDivElement> => {
    let overlays = IFRAME_OVERLAYS_BY_ENGINE.get(engine);
    if (!overlays) {
        overlays = new Map<number, HTMLDivElement>();
        IFRAME_OVERLAYS_BY_ENGINE.set(engine, overlays);
    }
    return overlays;
};

const clampNumber = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const formatPercent = (value: number): string => `${Number(clampNumber(value, 0, 100).toFixed(3))}%`;

const normalizeShapeType = (rawType: string | null | undefined): SupportedIframeShapeType | 'unsupported' => {
    const normalized = String(rawType ?? '').split('/').pop()?.toLowerCase() ?? '';
    if (normalized === 'rect' || normalized === 'ellipse' || normalized === 'polygon' || normalized === 'star') {
        return normalized;
    }
    return 'unsupported';
};

const buildRegularPolygonClipPath = (sides: number): string => {
    const safeSides = clampNumber(Math.round(sides) || 0, 3, 16);
    const points = Array.from({length: safeSides}, (_, index) => {
        const angle = -Math.PI / 2 + (index * Math.PI * 2) / safeSides;
        const x = 50 + 50 * Math.cos(angle);
        const y = 50 + 50 * Math.sin(angle);
        return `${formatPercent(x)} ${formatPercent(y)}`;
    });
    return `polygon(${points.join(', ')})`;
};

const normalizeStarInnerRatio = (value: number | null): number => {
    if (value == null || !Number.isFinite(value)) {
        return 0.5;
    }
    const normalized = value > 1 ? value / 100 : value;
    return clampNumber(normalized, 0.08, 0.95);
};

const buildStarClipPath = (points: number, innerRatio: number): string => {
    const safePoints = clampNumber(Math.round(points) || 0, 3, 16);
    const safeInnerRatio = clampNumber(innerRatio, 0.08, 0.95);
    const vertices = Array.from({length: safePoints * 2}, (_, index) => {
        const isOuter = index % 2 === 0;
        const angle = -Math.PI / 2 + (index * Math.PI) / safePoints;
        const radius = isOuter ? 50 : 50 * safeInnerRatio;
        const x = 50 + radius * Math.cos(angle);
        const y = 50 + radius * Math.sin(angle);
        return `${formatPercent(x)} ${formatPercent(y)}`;
    });
    return `polygon(${vertices.join(', ')})`;
};

const readShapeFloat = (engine: CreativeEngine, shapeId: number, key: string): number | null => {
    try {
        const value = engine.block.getFloat?.(shapeId, key);
        return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : null;
    } catch {
        return null;
    }
};

const readShapeInt = (engine: CreativeEngine, shapeId: number, key: string): number | null => {
    try {
        const value = engine.block.getInt?.(shapeId, key);
        return typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null;
    } catch {
        return null;
    }
};

const getIframeShapeId = (engine: CreativeEngine, blockId: number): number | null => {
    try {
        const shapeId = engine.block.getShape?.(blockId) ?? null;
        return shapeId != null && engine.block.isValid?.(shapeId) ? shapeId : null;
    } catch {
        return null;
    }
};

const readIframeRectCornerRadii = (engine: CreativeEngine, blockId: number): RectCornerRadii | null => {
    const shapeId = getIframeShapeId(engine, blockId);
    if (!shapeId) return null;

    const tl = readShapeFloat(engine, shapeId, 'shape/rect/cornerRadiusTL');
    const tr = readShapeFloat(engine, shapeId, 'shape/rect/cornerRadiusTR');
    const br = readShapeFloat(engine, shapeId, 'shape/rect/cornerRadiusBR');
    const bl = readShapeFloat(engine, shapeId, 'shape/rect/cornerRadiusBL');

    if (tl == null || tr == null || br == null || bl == null) {
        return null;
    }

    return {tl, tr, br, bl};
};

const applyUniformIframeRectCornerRadius = (engine: CreativeEngine, blockId: number, radius: number): void => {
    const shapeId = getIframeShapeId(engine, blockId);
    if (!shapeId) return;

    const normalizedRadius = Math.max(0, Math.round(radius));
    try { engine.block.setFloat?.(shapeId, 'shape/rect/cornerRadiusTL', normalizedRadius); } catch {}
    try { engine.block.setFloat?.(shapeId, 'shape/rect/cornerRadiusTR', normalizedRadius); } catch {}
    try { engine.block.setFloat?.(shapeId, 'shape/rect/cornerRadiusBR', normalizedRadius); } catch {}
    try { engine.block.setFloat?.(shapeId, 'shape/rect/cornerRadiusBL', normalizedRadius); } catch {}
};

const toBorderRadiusCss = (radii: RectCornerRadii | null, fallbackRadius: number): string => {
    if (!radii) {
        return `${Math.max(0, fallbackRadius)}px`;
    }

    return `${Math.max(0, radii.tl)}px ${Math.max(0, radii.tr)}px ${Math.max(0, radii.br)}px ${Math.max(0, radii.bl)}px`;
};

const readIframeShapeVisual = (
    engine: CreativeEngine,
    blockId: number,
    fallbackRadius: number
): IframeShapeVisual => {
    const shapeId = getIframeShapeId(engine, blockId);
    if (!shapeId) {
        return {
            type: 'rect',
            borderRadiusCss: `${Math.max(0, fallbackRadius)}px`,
            clipPathCss: null
        };
    }

    const shapeType = (() => {
        try {
            return normalizeShapeType(engine.block.getType(shapeId));
        } catch {
            return 'unsupported';
        }
    })();

    switch (shapeType) {
        case 'ellipse':
            return {type: 'ellipse', borderRadiusCss: '0px', clipPathCss: 'ellipse(50% 50% at 50% 50%)'};
        case 'polygon':
            return {
                type: 'polygon',
                borderRadiusCss: '0px',
                clipPathCss: buildRegularPolygonClipPath(readShapeInt(engine, shapeId, 'shape/polygon/sides') ?? 5)
            };
        case 'star':
            return {
                type: 'star',
                borderRadiusCss: '0px',
                clipPathCss: buildStarClipPath(
                    readShapeInt(engine, shapeId, 'shape/star/points') ?? 5,
                    normalizeStarInnerRatio(readShapeFloat(engine, shapeId, 'shape/star/innerDiameter'))
                )
            };
        case 'rect':
            return {
                type: 'rect',
                borderRadiusCss: toBorderRadiusCss(readIframeRectCornerRadii(engine, blockId), fallbackRadius),
                clipPathCss: null
            };
        case 'unsupported':
        default:
            return {
                type: 'unsupported',
                borderRadiusCss: `${Math.max(0, fallbackRadius)}px`,
                clipPathCss: null
            };
    }
};

const applyShapeMask = (
    element: HTMLElement | null,
    borderRadiusCss: string,
    clipPathCss: string | null
): void => {
    if (!element) return;
    element.style.borderRadius = clipPathCss ? '0px' : borderRadiusCss;
    const nextClipPath = clipPathCss ?? 'none';
    element.style.clipPath = nextClipPath;
    (element.style as any).webkitClipPath = nextClipPath;
};

export function isIframeBlock(
    engine: CreativeEngine,
    blockId: number | null | undefined
): blockId is number {
    if (!engine || blockId == null) return false;
    if (!Number.isInteger(blockId) || blockId <= 0 || blockId === INVALID_BLOCK_ID) return false;
    try {
        return engine.block.isValid?.(blockId)
            && engine.block.getMetadata?.(blockId, BLOCK_TYPE) === IFRAME_META_KEY
            && safeHasIframeMeta(engine, blockId);
    } catch {
        return false;
    }
}

const IFRAME_CARD_SVG = encodeURIComponent(`
<svg viewBox="0 0 258 214" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="#f8fbff"/>
      <stop offset="100%" stop-color="#e8eef9"/>
    </linearGradient>
    <linearGradient id="screen" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#dbeafe"/>
      <stop offset="100%" stop-color="#bfdbfe"/>
    </linearGradient>
  </defs>
  <rect width="258" height="214" rx="24" fill="url(#bg)"/>
  <rect x="28" y="26" width="202" height="136" rx="18" fill="#ffffff" stroke="#cdd7e8" stroke-width="3"/>
  <rect x="40" y="40" width="178" height="96" rx="14" fill="url(#screen)"/>
  <rect x="40" y="40" width="178" height="20" rx="10" fill="#eff6ff"/>
  <circle cx="58" cy="50" r="4" fill="#f97316"/>
  <circle cx="72" cy="50" r="4" fill="#facc15"/>
  <circle cx="86" cy="50" r="4" fill="#22c55e"/>
  <path d="M96 96h66" stroke="#2563eb" stroke-width="8" stroke-linecap="round"/>
  <path d="M84 112h90" stroke="#60a5fa" stroke-width="8" stroke-linecap="round"/>
  <path d="M106 78l-18 18 18 18" stroke="#1d4ed8" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <path d="M152 78l18 18-18 18" stroke="#1d4ed8" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
  <rect x="90" y="174" width="78" height="10" rx="5" fill="#94a3b8"/>
</svg>
`);

export function defaultStyle(): Required<NonNullable<IframeMeta['style']>> {
    return {
        borderRadiusPx: 0,
        backgroundColor: '#F8FAFC',
        outlineColor: 'rgba(15, 23, 42, 0.14)',
        outlineSizePx: 1,
    };
}

export function withDefaults(meta: Partial<IframeMeta>): IframeMeta {
    return {
        id: meta.id || `iframe-${Math.random().toString(36).slice(2, 8)}`,
        elementType: IFRAME_META_KEY,
        url: String(meta.url ?? '').trim(),
        style: {...defaultStyle(), ...(meta.style ?? {})},
    };
}

export function installIframePlugin(
    cesdk: CreativeEditorSDK,
    options: { mountInteractiveOverlay?: boolean; autoOpenLibrary?: boolean } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    addIframeAssetSource(cesdk);

    cesdk.ui.addAssetLibraryEntry({
        id: IFRAME_ENTRY_ID,
        sourceIds: [IFRAME_SOURCE_ID],
        title: () => 'Embeds',
        previewLength: 2,
        gridColumns: 2,
        gridItemHeight: 'square',
        cardBorder: false,
        cardLabel: () => 'Website Embed',
        cardLabelPosition: () => 'inside',
        cardLabelTruncateLines: () => 'single',
        cardLabelStyle: () => ({
            horizontalAlignment: 'center',
            color: '#0f172a',
            fontWeight: '700',
            background: 'rgba(255, 255, 255, 0.88)',
            padding: '6px 8px',
            borderRadius: '10px',
            textShadow: '0 1px 0 rgba(255,255,255,0.65)'
        }),
        cardBackgroundPreferences: [{type: 'image' as any, path: 'meta.thumbUri'}],
        previewBackgroundType: 'cover',
    });

    if (options.mountInteractiveOverlay !== false) {
        const engine = cesdk.engine;
        let overlayContainer = document.getElementById('overlay-container');
        if (!overlayContainer) {
            const host = document.getElementById("root-shadow");
            const root = host?.shadowRoot || (host as any)?.__shadowRoot;
            overlayContainer = root?.getElementById('overlay-container');
        }
        return mountIframeOverlays(engine, {
            container: overlayContainer,
            interactive: false,
            clearHTMLOverlays: true
        });
    }

    return {
        updateForActivePage: () => {},
        cleanup: () => {}
    };
}

function addIframeAssetSource(cesdk: CreativeEditorSDK): void {
    const engine = cesdk.engine as CreativeEngine;

    const source: any = {
        id: IFRAME_SOURCE_ID,
        findAssets: async (queryData: any) => ({
            assets: [
                {
                    id: 'idecide.iframe.embed',
                    locale: 'en',
                    label: 'Website Embed',
                    tags: ['iframe', 'embed', 'website'],
                    meta: {
                        thumbUri: `data:image/svg+xml;utf8,${IFRAME_CARD_SVG}`,
                        blockType: '//ly.img.ubq/graphic',
                        width: 960,
                        height: 540
                    },
                    context: {sourceId: IFRAME_SOURCE_ID}
                }
            ],
            total: 1,
            currentPage: queryData?.page ?? 0,
            nextPage: undefined
        }),
        applyAsset: async (_assetResult: AssetResult) => {
            const page = engine.scene.getCurrentPage?.();
            if (!page) return;

            const block = createIframeBlock(engine, page);
            cesdk.engine.block.setSelected(block, true);
            cesdk.engine.editor.addUndoStep();
        }
    };

    try {
        const existing: any = (engine as any).asset?.findAllSources?.();
        if (!existing || (Array.isArray(existing) && !existing.includes(IFRAME_SOURCE_ID))) {
            engine.asset.addSource(source);
        }
    } catch (error) {
        console.error('Error adding iframe asset source.', error);
    }
}

export function mountIframeOverlays(
    engine: CreativeEngine,
    opts: { container?: HTMLElement | null; interactive?: boolean; clearHTMLOverlays?: boolean } = {}
): { updateForActivePage: (pageId: number) => void; cleanup: () => void } {
    const host = opts.container ?? (engine as any).element?.parentElement ?? document.body;
    let clearOverlays = opts.clearHTMLOverlays;
    let currentPageId = engine.scene?.getCurrentPage?.() ?? 0;

    const overlays = getIframeOverlays(engine);

    const isIframeOverlayValid = (iframeId: number): boolean => {
        if (!currentPageId) return false;
        try {
            if (!isIframeBlock(engine, iframeId)) return false;
            return findOwningPage(engine, iframeId) === currentPageId;
        } catch {
            return false;
        }
    };

    const removeTrackedOverlay = (iframeId: number, el?: HTMLDivElement | null) => {
        teardownIframeOverlay(el);
        try { el?.remove(); } catch {}
        overlays.delete(iframeId);
    };

    const purgeInvalidTrackedOverlays = () => {
        [...overlays.entries()].forEach(([id, el]) => {
            if (el.parentElement !== host || !el.isConnected || !isIframeOverlayValid(id)) {
                removeTrackedOverlay(id, el);
                return;
            }

            const ownerPage = findOwningPage(engine, id);
            el.dataset.ownerPage = ownerPage != null ? String(ownerPage) : '';
        });
    };

    const removeStrayOverlays = () => {
        purgeInvalidTrackedOverlays();

        const domOverlays = Array.from(host.querySelectorAll('[data-idecide-overlay="iframe"]')) as HTMLDivElement[];
        domOverlays.forEach((el) => {
            const blockId = Number(el.dataset.blockId ?? '');
            const tracked = Number.isFinite(blockId) ? overlays.get(blockId) : null;
            if (tracked !== el || !Number.isFinite(blockId) || !isIframeOverlayValid(blockId)) {
                teardownIframeOverlay(el);
                try { el.remove(); } catch {}
                if (tracked === el && Number.isFinite(blockId)) {
                    overlays.delete(blockId);
                }
            }
        });

        purgeInvalidTrackedOverlays();
    };

    const clearMountedOverlays = () => {
        overlays.forEach((el) => {
            teardownIframeOverlay(el);
            try { el.remove(); } catch {}
        });
        overlays.clear();
        removeStrayOverlays();
    };

    const findActivePageIframes = (): number[] => {
        if (!currentPageId) return [];
        return findAllIframes(engine).filter((id) => isIframeOverlayValid(id));
    };

    const rebuild = () => {
        removeStrayOverlays();
        const all: number[] = findActivePageIframes();

        if (clearOverlays) {
            clearMountedOverlays();
            clearOverlays = false;
        } else {
            [...overlays.keys()].forEach((id) => {
                if (!all.includes(id)) {
                    removeTrackedOverlay(id, overlays.get(id));
                }
            });
        }

        all.forEach((id) => {
            if (!overlays.has(id)) {
                const el = createIframeOverlay(engine, id, host, !!opts.interactive);
                overlays.set(id, el);
                requestAnimationFrame(() => positionOverlayToBlock(engine, id, el, updateIframeOverlayGeometry));
                return;
            }

            const existing = overlays.get(id);
            if (existing) {
                syncIframeOverlayContent(engine, id, existing, existing.dataset.interactive === 'true');
            }
        });
    };

    const layoutAll = () => {
        removeStrayOverlays();
        [...overlays.entries()].forEach(([id, el]) => {
            if (!isIframeOverlayValid(id)) {
                removeTrackedOverlay(id, el);
                return;
            }
            positionOverlayToBlock(engine, id, el, updateIframeOverlayGeometry);
        });
    };

    let scheduled = false;
    const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
            scheduled = false;
            rebuild();
            layoutAll();
            updateOverlayTimelineVisibility(engine, overlays, isEngineReady);
        });
    };

    let currentSubscriptionIds: number[] = [];
    let unsubIframes: (() => void) | null = null;

    const updateIframeSubscriptions = () => {
        const iframes = findActivePageIframes();
        const subscriptionIds = Array.from(new Set(
            iframes.flatMap((id) => {
                const shapeId = getIframeShapeId(engine, id);
                return shapeId != null ? [id, shapeId] : [id];
            })
        ));

        if (JSON.stringify(subscriptionIds) !== JSON.stringify(currentSubscriptionIds)) {
            if (unsubIframes) {
                try { unsubIframes(); } catch {}
            }

            currentSubscriptionIds = subscriptionIds;
            unsubIframes = subscriptionIds.length > 0
                ? engine.event?.subscribe?.(subscriptionIds, (events) => {
                    try {
                        const validEvents = events.filter((event) => {
                            try {
                                const id = event.block;
                                if (!Number.isInteger(id) || id <= 0 || id === INVALID_BLOCK_ID) {
                                    return false;
                                }
                                return engine.block.isValid?.(id);
                            } catch {
                                return false;
                            }
                        });

                        if (validEvents.some((event) => event.type === 'Updated')) {
                            schedule();
                            requestAnimationFrame(() => {
                                updateIframeSubscriptions();
                            });
                        }
                    } catch (error) {
                        console.warn('Iframe event handler error:', error);
                    }
                })
                : null;
        }
    };

    let unsubPages: (() => void) | null = null;

    const updateForActivePage = (pageId: number) => {
        currentPageId = pageId;
        clearMountedOverlays();

        if (unsubPages) {
            try { unsubPages(); } catch {}
        }

        unsubPages = engine.event?.subscribe?.([pageId], (events) => {
            try {
                const validEvents = events.filter((event) => {
                    try {
                        const id = event.block;
                        if (!Number.isInteger(id) || id <= 0 || id === INVALID_BLOCK_ID) {
                            return false;
                        }
                        return engine.block.isValid?.(id);
                    } catch {
                        return false;
                    }
                });

                if (validEvents.some((event) => event.type === 'Updated')) {
                    schedule();
                    requestAnimationFrame(() => {
                        updateIframeSubscriptions();
                    });
                }
            } catch (error) {
                console.warn('Iframe page event handler error:', error);
            }
        });

        updateIframeSubscriptions();

        requestAnimationFrame(() => {
            schedule();
            layoutAll();
            updateOverlayTimelineVisibility(engine, overlays, isEngineReady);
        });
    };

    const initialPage = engine.scene?.getCurrentPage?.();
    if (initialPage) {
        updateForActivePage(initialPage);
    } else {
        schedule();
        updateIframeSubscriptions();
    }

    const handleResize = () => {
        requestAnimationFrame(() => {
            layoutAll();
        });
    };

    const ro = new ResizeObserver(handleResize);
    ro.observe(host);

    const wrapper = host.closest('.cesdkWrapperStyle');
    if (wrapper) {
        ro.observe(wrapper);
    }

    window.addEventListener('cesdk-relayout', handleResize);

    let zoomTimeout: number | null = null;
    const unsubZoomLevelChanged = engine.scene?.onZoomLevelChanged?.(() => {
        if (zoomTimeout) clearTimeout(zoomTimeout);
        zoomTimeout = window.setTimeout(() => {
            layoutAll();
        }, 50);
    });

    let visibilityRafId: number | null = null;
    let unsubStateChanged: (() => void) | null = null;

    if (findAllIframes(engine).length > 0) {
        const rootEl = document.getElementById('root-shadow');
        const shadowRoot = rootEl?.shadowRoot;
        const playheadElement = shadowRoot?.querySelector('[role="slider"][aria-valuenow]');
        const reviewWrapper = document.querySelector('.review-cesdk-wrapper[data-playback-time]');
        const timeElement = playheadElement || reviewWrapper;

        if (timeElement) {
            const timeAttribute = playheadElement ? 'aria-valuenow' : 'data-playback-time';
            let lastProcessedTime = -1;

            const timeObserver = new MutationObserver((mutations) => {
                const timeChanged = mutations.some((mutation) =>
                    mutation.type === 'attributes' && mutation.attributeName === timeAttribute
                );

                if (!timeChanged) return;

                const currentTimeValue = timeElement.getAttribute(timeAttribute);
                if (!currentTimeValue) return;

                const parsedTime = parseFloat(currentTimeValue);
                const roundedTime = Math.round(parsedTime * 10) / 10;
                if (roundedTime === lastProcessedTime) return;

                lastProcessedTime = roundedTime;

                if (visibilityRafId !== null) {
                    cancelAnimationFrame(visibilityRafId);
                }

                visibilityRafId = requestAnimationFrame(() => {
                    visibilityRafId = null;
                    updateOverlayTimelineVisibility(engine, overlays, isEngineReady);
                });
            });

            timeObserver.observe(timeElement, {
                attributes: true,
                attributeFilter: [timeAttribute]
            });

            unsubStateChanged = () => {
                timeObserver.disconnect();
            };
        }
    }

    updateOverlayTimelineVisibility(engine, overlays, isEngineReady);

    return {
        updateForActivePage,
        cleanup: () => {
            window.removeEventListener('cesdk-relayout', handleResize);
            try { unsubIframes?.(); } catch {}
            try { unsubPages?.(); } catch {}
            try { ro.disconnect(); } catch {}
            try { unsubZoomLevelChanged?.(); } catch {}
            if (zoomTimeout) clearTimeout(zoomTimeout);
            try { unsubStateChanged?.(); } catch {}
            if (visibilityRafId !== null) {
                cancelAnimationFrame(visibilityRafId);
                visibilityRafId = null;
            }
            clearMountedOverlays();
        }
    };
}

export function createIframeBlock(
    engine: CreativeEngine,
    parentPage: number,
    meta?: Partial<IframeMeta>
): number {
    const id = `iframe-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const iframeMeta = withDefaults({id, ...meta});

    const block = engine.block.create('graphic');
    setIframeMeta(engine, block, iframeMeta);
    engine.block.setName?.(block, iframeMeta.url ? `Iframe - ${iframeMeta.url}` : 'Iframe');

    const pageW = engine.block.getWidth?.(parentPage) ?? 1920;
    const pageH = engine.block.getHeight?.(parentPage) ?? 1080;
    const w = Math.round(pageW * 0.64);
    const h = Math.round(pageH * 0.46);
    const x = Math.round((pageW - w) / 2);
    const y = Math.round((pageH - h) / 2);

    engine.block.setWidthMode?.(block, 'Absolute');
    engine.block.setHeightMode?.(block, 'Absolute');
    engine.block.setWidth?.(block, w);
    engine.block.setHeight?.(block, h);
    engine.block.setPositionX?.(block, x);
    engine.block.setPositionY?.(block, y);

    try {
        const shape = engine.block.createShape?.('rect');
        engine.block.setShape?.(block, shape);
        applyUniformIframeRectCornerRadius(engine, block, iframeMeta.style?.borderRadiusPx ?? 0);

        if (typeof engine.block.setFillEnabled === 'function') {
            try { engine.block.setFillEnabled(block, false); } catch {}
        }
        if (typeof engine.block.setStrokeEnabled === 'function') {
            try { engine.block.setStrokeEnabled(block, false); } catch {}
        }
    } catch {}

    engine.block.appendChild(parentPage, block);
    engine.block.setMetadata(block, BLOCK_TYPE, IFRAME_META_KEY);
    engine.block.setScopeEnabled(block, 'layer/rotate', false);

    return block;
}

export function setIframeMeta(engine: CreativeEngine, blockId: number, meta: IframeMeta): void {
    const normalized = withDefaults(meta);
    engine.block.setMetadata(blockId, IFRAME_META_KEY, JSON.stringify(normalized));
    engine.block.setName?.(blockId, normalized.url ? `Iframe - ${normalized.url}` : 'Iframe');
}

export function getIframeMeta(engine: CreativeEngine, blockId: number): IframeMeta {
    try {
        const raw = (engine.block as any).getMetadata?.(blockId, IFRAME_META_KEY);
        if (raw) return withDefaults(JSON.parse(raw));
    } catch {}
    return withDefaults({id: `iframe-${blockId}`});
}

export function safeHasIframeMeta(engine: CreativeEngine, id: number): boolean {
    try {
        const raw = (engine.block as any).getMetadata?.(id, IFRAME_META_KEY);
        if (!raw) return false;
        try { JSON.parse(raw); } catch { return false; }
        return true;
    } catch {
        return false;
    }
}

export function findAllIframes(engine: CreativeEngine): number[] {
    const pages: number[] = engine.scene?.getPages?.() ?? [];
    const out: number[] = [];
    const stack: number[] = [...pages];

    while (stack.length) {
        const id = stack.pop()!;
        if (!Number.isInteger(id) || id <= 0 || id === INVALID_BLOCK_ID) {
            continue;
        }

        try {
            if (!engine.block.isValid?.(id)) {
                continue;
            }

            const children: number[] = engine.block.getChildren?.(id) ?? [];
            for (const child of children) stack.push(child);

            if (isIframeBlock(engine, id)) {
                out.push(id);
            }
        } catch {
            // Ignore invalid blocks during traversal.
        }
    }

    return out;
}

const resolveIframeUrl = (rawUrl?: string): string => {
    const normalized = normalizeResolvedUrl(rawUrl);
    if (!normalized) return '';

    if (normalized === 'about:blank') return normalized;
    if (normalized.startsWith('/')) return normalized;

    if (/^https?:\/\//i.test(normalized)) {
        try {
            new URL(normalized);
            return normalized;
        } catch {
            return '';
        }
    }

    return '';
};

const setIframeElementSource = (iframeEl: HTMLIFrameElement | null, src: string) => {
    if (!iframeEl) return;
    const nextSrc = src || 'about:blank';
    if ((iframeEl.getAttribute('src') ?? '') === nextSrc) return;
    iframeEl.setAttribute('src', nextSrc);
};

const teardownIframeOverlay = (_el?: HTMLDivElement | null) => {};

const syncIframeOverlayContent = (
    engine: CreativeEngine,
    blockId: number,
    wrap: HTMLDivElement,
    interactive: boolean
) => {
    const meta = getIframeMeta(engine, blockId);
    const style = {...defaultStyle(), ...(meta.style ?? {})};
    const normalizedUrl = resolveIframeUrl(meta.url);
    const shapeVisual = readIframeShapeVisual(engine, blockId, style.borderRadiusPx ?? 0);

    const shell = wrap.querySelector('[data-role="shell"]') as HTMLDivElement | null;
    const content = wrap.querySelector('[data-role="content"]') as HTMLDivElement | null;
    const iframeEl = wrap.querySelector('[data-role="iframe"]') as HTMLIFrameElement | null;
    const placeholder = wrap.querySelector('[data-role="placeholder"]') as HTMLDivElement | null;
    const placeholderTitle = wrap.querySelector('[data-role="placeholder-title"]') as HTMLDivElement | null;
    const placeholderBody = wrap.querySelector('[data-role="placeholder-body"]') as HTMLDivElement | null;

    wrap.dataset.normalizedUrl = normalizedUrl;
    wrap.dataset.borderRadiusCss = shapeVisual.borderRadiusCss;
    wrap.dataset.clipPathCss = shapeVisual.clipPathCss ?? '';
    wrap.dataset.outlineSizePx = String(Math.max(0, style.outlineSizePx ?? 0));

    if (shell) {
        shell.style.background = style.outlineColor;
        shell.style.boxShadow = normalizedUrl
            ? '0 12px 32px rgba(15, 23, 42, 0.14)'
            : '0 14px 36px rgba(15, 23, 42, 0.1)';
        shell.style.pointerEvents = interactive ? 'auto' : 'none';
    }

    if (content) {
        content.style.background = style.backgroundColor;
        content.style.pointerEvents = interactive ? 'auto' : 'none';
    }

    if (placeholder) {
        placeholder.style.display = normalizedUrl ? 'none' : 'flex';
    }

    if (placeholderTitle) {
        placeholderTitle.textContent = normalizedUrl ? 'Loading website...' : 'Add a website URL';
    }

    if (placeholderBody) {
        placeholderBody.textContent = normalizedUrl
            ? normalizedUrl
            : 'Select this iframe block, then paste a full URL in Options.';
    }

    if (iframeEl) {
        iframeEl.style.pointerEvents = interactive ? 'auto' : 'none';
        iframeEl.title = normalizedUrl ? `Embedded website: ${normalizedUrl}` : 'Embedded website';
        setIframeElementSource(iframeEl, normalizedUrl);
    }
};

export function createIframeOverlay(
    engine: CreativeEngine,
    blockId: number,
    root: HTMLElement,
    interactive: boolean
): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.style.position = 'absolute';
    wrap.style.pointerEvents = 'none';
    wrap.style.willChange = 'left,top,width,height';
    // Keep embedded websites above generic click-action hit areas so iframe interactions
    // do not trigger overlapping block actions beneath them.
    wrap.style.zIndex = '1';
    wrap.dataset.idecideOverlay = 'iframe';
    wrap.dataset.blockId = String(blockId);
    wrap.dataset.interactive = interactive ? 'true' : 'false';

    const ownerPage = findOwningPage(engine, blockId);
    wrap.dataset.ownerPage = ownerPage != null ? String(ownerPage) : '';

    root.appendChild(wrap);

    const shell = document.createElement('div');
    shell.dataset.role = 'shell';
    Object.assign(shell.style, {
        position: 'absolute',
        inset: '0',
        overflow: 'hidden',
        boxSizing: 'border-box',
        pointerEvents: interactive ? 'auto' : 'none'
    });

    const content = document.createElement('div');
    content.dataset.role = 'content';
    Object.assign(content.style, {
        position: 'absolute',
        inset: `${defaultStyle().outlineSizePx}px`,
        overflow: 'hidden',
        background: defaultStyle().backgroundColor,
        boxSizing: 'border-box',
        pointerEvents: interactive ? 'auto' : 'none'
    });

    const iframeEl = document.createElement('iframe');
    iframeEl.dataset.role = 'iframe';
    iframeEl.loading = 'lazy';
    iframeEl.referrerPolicy = 'strict-origin-when-cross-origin';
    iframeEl.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen';
    iframeEl.setAttribute('allowfullscreen', 'true');
    Object.assign(iframeEl.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        border: '0',
        background: '#ffffff',
        pointerEvents: interactive ? 'auto' : 'none'
    });

    const placeholder = document.createElement('div');
    placeholder.dataset.role = 'placeholder';
    Object.assign(placeholder.style, {
        position: 'absolute',
        inset: '0',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '10px',
        padding: '24px',
        textAlign: 'center',
        background: 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(237,242,247,0.98) 100%)',
        color: '#334155',
        pointerEvents: 'none'
    });

    const placeholderTitle = document.createElement('div');
    placeholderTitle.dataset.role = 'placeholder-title';
    placeholderTitle.style.fontWeight = '800';
    placeholderTitle.style.letterSpacing = '0.02em';

    const placeholderBody = document.createElement('div');
    placeholderBody.dataset.role = 'placeholder-body';
    Object.assign(placeholderBody.style, {
        maxWidth: '90%',
        lineHeight: '1.45',
        wordBreak: 'break-word',
        opacity: '0.82'
    });

    placeholder.appendChild(placeholderTitle);
    placeholder.appendChild(placeholderBody);
    content.appendChild(iframeEl);
    content.appendChild(placeholder);
    shell.appendChild(content);
    wrap.appendChild(shell);

    syncIframeOverlayContent(engine, blockId, wrap, interactive);

    return wrap;
}

export function updateIframeOverlayGeometry(el: HTMLElement): void {
    const shell = el.querySelector('[data-role="shell"]') as HTMLDivElement | null;
    const content = el.querySelector('[data-role="content"]') as HTMLDivElement | null;
    const iframeEl = el.querySelector('[data-role="iframe"]') as HTMLIFrameElement | null;
    const placeholder = el.querySelector('[data-role="placeholder"]') as HTMLDivElement | null;
    const placeholderTitle = el.querySelector('[data-role="placeholder-title"]') as HTMLDivElement | null;
    const placeholderBody = el.querySelector('[data-role="placeholder-body"]') as HTMLDivElement | null;

    const width = el.clientWidth || parseFloat(el.style.width) || 0;
    const height = el.clientHeight || parseFloat(el.style.height) || 0;
    const minSide = Math.max(1, Math.min(width || 1, height || 1));
    const borderRadiusCss = el.dataset.borderRadiusCss ?? '0px';
    const clipPathCss = el.dataset.clipPathCss || null;
    const rawOutlineSize = Number(el.dataset.outlineSizePx ?? 0);
    const outlineSize = clampNumber(rawOutlineSize, 0, Math.max(0, Math.floor(minSide / 2) - 1));

    applyShapeMask(shell, borderRadiusCss, clipPathCss);
    applyShapeMask(content, borderRadiusCss, clipPathCss);
    applyShapeMask(iframeEl, borderRadiusCss, clipPathCss);
    applyShapeMask(placeholder, borderRadiusCss, clipPathCss);

    if (content) {
        content.style.inset = `${outlineSize}px`;
    }
    if (placeholder) {
        placeholder.style.padding = `${Math.max(18, Math.round(minSide * 0.08))}px`;
    }
    if (placeholderTitle) {
        placeholderTitle.style.fontSize = `${Math.max(18, Math.min(28, Math.round(minSide * 0.11)))}px`;
    }
    if (placeholderBody) {
        placeholderBody.style.fontSize = `${Math.max(12, Math.min(16, Math.round(minSide * 0.045)))}px`;
    }
}

export function refreshIframeOverlay(engine: CreativeEngine, blockId: number): void {
    const overlays = getIframeOverlays(engine);
    const existing = overlays.get(blockId);
    if (!existing) return;

    syncIframeOverlayContent(engine, blockId, existing, existing.dataset.interactive === 'true');
    requestAnimationFrame(() => {
        positionOverlayToBlock(engine, blockId, existing, updateIframeOverlayGeometry);
    });
}
